import React, { useState, useMemo } from 'react';
import { formatUsd, tierPricing, isStalled, chatSpendSince } from '../../utils/agentCost';

/**
 * Everything one agent has actually done.
 *
 * The dashboard answers "is this agent expensive"; it cannot answer "expensive
 * doing what", and a row reading "1 failed" with no way to open it is worse
 * than no row at all — it tells a user something is wrong and nothing about
 * what. This is the way in: every run, newest first, each opening onto the task
 * it was given, the answer it gave, what it cost and why it stopped.
 *
 * Chat is listed beside the runs rather than in a separate place, because from
 * the user's side a conversation and a pipeline task are the same agent doing
 * the same kind of work, and splitting them is how the two cost figures stopped
 * agreeing in the first place.
 */

const STATUS_META = {
  running:   { label: 'Working',  cls: 'run' },
  completed: { label: 'Finished', cls: 'ok' },
  capped:    { label: 'Hit its cap', cls: 'warn' },
  failed:    { label: 'Failed',   cls: 'bad' },
  resumed:   { label: 'Finished', cls: 'ok' },
};

function when(iso) {
  const t = Date.parse(iso || '');
  if (isNaN(t)) return '';
  return new Date(t).toLocaleString();
}

function took(run) {
  const a = Date.parse(run.startedAt || '');
  const b = Date.parse(run.completedAt || '');
  if (isNaN(a) || isNaN(b) || b < a) return '';
  const secs = Math.round((b - a) / 1000);
  if (secs < 90) return `${secs}s`;
  return `${Math.round(secs / 60)} min`;
}

function RunRow({ run, now }) {
  const [open, setOpen] = useState(false);
  // A run still marked running long after it started is not running. Saying so
  // here matters more than on the dashboard: this is where someone comes to
  // find out why nothing happened.
  const stalled = isStalled(run, now);
  const meta = stalled
    ? { label: 'Stuck', cls: 'warn' }
    : (STATUS_META[run.status] || { label: run.status || 'Unknown', cls: '' });

  return (
    <div className={`agent-run${open ? ' is-open' : ''}`}>
      <button className="agent-run-head" onClick={() => setOpen(o => !o)}>
        <span className={`agent-run-status ${meta.cls}`}>{meta.label}</span>
        <span className="agent-run-task">
          {String(run.task || run.summary || 'No task recorded').replace(/\s+/g, ' ').slice(0, 90)}
        </span>
        <span className="agent-run-when">{when(run.startedAt)}</span>
        <span className="agent-run-cost">{run.usdCost > 0 ? formatUsd(run.usdCost) : '—'}</span>
        <span className="agent-run-chev">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="agent-run-body">
          <dl className="agent-run-facts">
            {run.functionLabel && <><dt>Team</dt><dd>{run.functionLabel}</dd></>}
            {run.tier && <><dt>Tier</dt><dd>{tierPricing(run.tier).label}</dd></>}
            {took(run) && <><dt>Took</dt><dd>{took(run)}</dd></>}
            {run.capUsd > 0 && (
              <><dt>Cap</dt>
                <dd>
                  {formatUsd(run.capUsd)}
                  {run.status === 'capped' && ' — reached, so the work is unfinished'}
                </dd>
              </>
            )}
            {run.tokens && (
              <><dt>Tokens</dt>
                <dd>
                  {(run.tokens.input || 0).toLocaleString()} in,{' '}
                  {(run.tokens.output || 0).toLocaleString()} out
                </dd>
              </>
            )}
            {run.lastToolCall && <><dt>Last asked for</dt><dd>{run.lastToolCall}</dd></>}
            {run.proposedKnowledge && (
              <><dt>Proposed</dt>
                <dd title="Waiting for review on the Knowledge tab">
                  “{run.proposedKnowledge}” — needs review
                </dd>
              </>
            )}
            {run.stopReason && <><dt>Stopped because</dt><dd>{run.stopReason}</dd></>}
          </dl>

          {run.task && (
            <div className="agent-run-block">
              <div className="agent-run-block-title">What it was asked</div>
              <pre className="agent-run-pre">{run.task}</pre>
            </div>
          )}

          {run.error && (
            <div className="agent-run-block">
              <div className="agent-run-block-title bad">Why it failed</div>
              <pre className="agent-run-pre bad">{run.error}</pre>
            </div>
          )}

          {run.result ? (
            <div className="agent-run-block">
              <div className="agent-run-block-title">What it said</div>
              <pre className="agent-run-pre">{run.result}</pre>
            </div>
          ) : run.status === 'running' && (
            <div className="agent-run-note">Still working — nothing to show yet.</div>
          )}

          {stalled && !run.error && (
            <div className="agent-run-note warn">
              Started {when(run.startedAt)} and never reported back. The work may
              well have been done — the result just never reached us.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatRow({ chat, now }) {
  const dayAgo = now - 24 * 60 * 60 * 1000;
  const recent = chatSpendSince(chat, dayAgo);
  return (
    <div className="agent-run is-chat">
      <div className="agent-run-head static">
        <span className="agent-run-status chat">Conversation</span>
        <span className="agent-run-task">
          {chat.turns ? `${chat.turns} messages` : 'Ongoing conversation'}
          {recent > 0 && <span className="agent-run-sub"> · {formatUsd(recent)} in the last 24h</span>}
        </span>
        <span className="agent-run-when">{when(chat.lastMessageAt)}</span>
        <span className="agent-run-cost">{chat.usdCost > 0 ? formatUsd(chat.usdCost) : '—'}</span>
      </div>
    </div>
  );
}

export default function AgentRunsPanel({ agent, fnNode, runs = [], chats = [], now, onClose }) {
  const mine = useMemo(
    () => runs
      .filter(r => r.agentId === agent.id)
      // Newest first: the run someone came here to look at is almost always the
      // last one.
      .sort((a, b) => String(b.startedAt || '').localeCompare(String(a.startedAt || ''))),
    [runs, agent.id],
  );
  const myChats = useMemo(
    () => chats.filter(c => c.agentId === agent.id),
    [chats, agent.id],
  );

  const failed = mine.filter(r => r.status === 'failed').length;
  const capped = mine.filter(r => r.status === 'capped').length;

  return (
    <div className="agent-runs-overlay" onClick={onClose}>
      <div className="agent-runs-panel" onClick={e => e.stopPropagation()}>
        <div className="agent-runs-head">
          <div>
            <div className="agent-runs-title">🤖 {agent.name || 'Agent'}</div>
            <div className="agent-runs-sub">
              {fnNode?.data?.label || ''} · {tierPricing(agent.model).label}
              {agent.role ? ` · ${agent.role}` : ''}
            </div>
          </div>
          <button className="agent-runs-close" onClick={onClose} title="Close">×</button>
        </div>

        {(failed > 0 || capped > 0) && (
          <div className="agent-runs-banner">
            {failed > 0 && `${failed} run${failed === 1 ? '' : 's'} failed`}
            {failed > 0 && capped > 0 && ' · '}
            {capped > 0 && `${capped} stopped at the spend cap, so the work is unfinished`}
            . Open one below to see what happened.
          </div>
        )}

        <div className="agent-runs-list">
          {mine.length === 0 && myChats.length === 0 && (
            <div className="agent-runs-empty">
              Nothing yet. This agent runs when a pipeline step hands it work, or
              when you ask it something.
            </div>
          )}
          {mine.map(r => <RunRow key={r.id} run={r} now={now} />)}
          {myChats.map(c => <ChatRow key={c.id} chat={c} now={now} />)}
        </div>
      </div>
    </div>
  );
}
